import React, { useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { getTranslatedString } from 'helpers/i18n';
import { Formik } from 'formik';
import { toFriendlyDateString } from 'helpers/Formatter';

import { PageContainer, PageHeaderTitle, PageHeaderDetails } from '../common/Page';
import PillButton from 'components/ui/Buttons/PillButton';
import TextButton from 'components/ui/Buttons/TextButton';
import RenewalProposalCard from '../review_renewal_details/RenewalProposalCard';
import RentersInsuranceCard from '../review_renewal_details/RentersInsuranceCard';
import LeasePricingCard from '../review_renewal_details/LeasePricingCard';
import NoticeOfIncreaseCard from '../review_renewal_details/NoticeOfIncreaseCard.js';
import ScheduleACard from '../review_renewal_details/ScheduleACard';
import ServicesAndUtilitiesCard from '../review_renewal_details/ServicesAndUtilitiesCard';
import Loading from '../Loading';
import Error from '../Error';
import ConfirmationDialog from 'components/ui/Dialogs/ConfirmationDialog';

import { media } from 'helpers/MediaHelper';
import { hostIsFourStar } from 'helpers/CurrentWindowLocation';

import { storeDetails, createRenewal, fetchLeaseCotenants } from '../../store/actions';
import LeaseDocumentTypes from '../review_renewal_details/LeaseDocumentTypes';

const PAGE_STATES = {
  loading: 'loading',
  error: 'error',
  form: 'form',
};

const CANCEL_RENEWAL_STATES = {
  confirm: 'confirm',
  success: 'success',
};

const StyledFooter = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
  ${media.mobile`
    flex-direction: column-reverse;
    height: 100px;
  `};
`;

const FORM_NAME = 'renewal_lease_form';

const ReviewRenewalDetails = ({
  selectedProposal,
  renewalDetails,
  storeDetails,
  closeWizard,
  pageIndex,
  setPageIndex,
  hideWizardControls,
  showWizardControls,
  lastPageIndex,
  selectedLeaseTerm,
  createRenewal,
  leaseId,
  fetchLeaseCotenants,
  leaseDocumentTypes,
}) => {
  const [pageState, setPageState] = useState(PAGE_STATES.form);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelDialogState, setCancelDialogState] = useState(CANCEL_RENEWAL_STATES.confirm);

  const moveForward = () => {
    if (pageIndex !== lastPageIndex) {
      return setPageIndex(pageIndex + 1);
    }
  };

  const handleSubmit = async values => {
    const details = await storeDetails(values);
    let data = {
      leaseId: leaseId,
      selectedProposal: selectedProposal,
      selectedLeaseTerm: selectedLeaseTerm,
      details: details,
    };
    try {
      const renewals = await createRenewal(data);
      await fetchLeaseCotenants(renewals.lease_id);
      setPageState(PAGE_STATES.form);
      moveForward();
    } catch (err) {
      hideWizardControls();
      setPageState(PAGE_STATES.error);
    }
  };

  const handleErrorBack = () => {
    showWizardControls();
    setPageState(PAGE_STATES.form);
  };

  const openCancelDialog = () => {
    setCancelDialogState(CANCEL_RENEWAL_STATES.confirm);
    setShowCancelDialog(true);
  };

  const closeCancelDialog = () => {
    setShowCancelDialog(false);
  };

  const closeWizardOnCancelSuccess = () => {
    closeCancelDialog();
    closeWizard();
  };

  if (pageState === PAGE_STATES.form) {
    return (
      <Formik
        initialValues={renewalDetails}
        onSubmit={values => {
          hideWizardControls();
          setPageState(PAGE_STATES.loading);
          handleSubmit(values);
        }}
      >
        {({ handleSubmit, ...formikBag }) => {
          return (
            <form id={FORM_NAME} onSubmit={handleSubmit}>
              <PageContainer>
                <PageHeaderTitle>
                  {getTranslatedString('landlordPortal.rentRoll.renewals.reviewDetailsTitle')}
                </PageHeaderTitle>
                <PageHeaderDetails>
                  {getTranslatedString('landlordPortal.rentRoll.renewals.reviewDetailsDesc')}
                </PageHeaderDetails>

                <RenewalProposalCard
                  leaseStartDate={toFriendlyDateString(selectedProposal.lease_from_date)}
                  leaseEndDate={toFriendlyDateString(selectedProposal.lease_to_date)}
                  selectedLeaseTerm={selectedLeaseTerm}
                  rent={selectedProposal.rent}
                  special={selectedProposal.special}
                  other={selectedProposal.other}
                />

                {!hostIsFourStar() && <RentersInsuranceCard {...formikBag} />}

                <LeasePricingCard
                  monthlyRent={selectedProposal.rent}
                  specialConcession={selectedProposal.special}
                  charges={selectedProposal.charges}
                />

                <NoticeOfIncreaseCard {...formikBag} />

                {!hostIsFourStar() && <ScheduleACard {...formikBag} />}

                {!hostIsFourStar() && <ServicesAndUtilitiesCard {...formikBag} />}

                {hostIsFourStar() && (
                  <LeaseDocumentTypes {...formikBag} leaseDocumentTypes={leaseDocumentTypes} />
                )}

                {/* ConfirmationDialog when cancelling renewal
                Dialog does not have loading and error state as
                it is only a confirmation and no api call */}
                <ConfirmationDialog
                  showConfirmationDialog={showCancelDialog}
                  dialogState={cancelDialogState}
                  confirm={{
                    primaryBtnText: getTranslatedString(
                      'landlordPortal.rentRoll.renewals.cancelConfirmation.confirm'
                    ),
                    headerText: getTranslatedString(
                      'landlordPortal.rentRoll.renewals.cancelConfirmation.header'
                    ),
                    bodyText: getTranslatedString(
                      'landlordPortal.rentRoll.renewals.cancelConfirmation.body'
                    ),
                    primaryAction: () => setCancelDialogState(CANCEL_RENEWAL_STATES.success),
                    secondaryBtnText: getTranslatedString('close'),
                    secondaryAction: closeCancelDialog,
                  }}
                  success={{
                    primaryBtnText: getTranslatedString(
                      'landlordPortal.rentRoll.renewals.successButton'
                    ),
                    headerText: getTranslatedString(
                      'landlordPortal.rentRoll.renewals.cancelRenewalSuccess'
                    ),
                    bodyText: getTranslatedString(
                      'landlordPortal.rentRoll.renewals.cancelRenewalSuccessBody'
                    ),
                    action: closeWizardOnCancelSuccess,
                  }}
                />

                <StyledFooter>
                  <TextButton onClick={openCancelDialog}>
                    {getTranslatedString('landlordPortal.rentRoll.renewals.cancelRenewal')}
                  </TextButton>
                  <PillButton form={FORM_NAME} type="submit">
                    {getTranslatedString('landlordPortal.rentRoll.renewals.createRenewal')}
                  </PillButton>
                </StyledFooter>
              </PageContainer>
            </form>
          );
        }}
      </Formik>
    );
  } else if (pageState === PAGE_STATES.loading) {
    return (
      <Loading
        bodyText={getTranslatedString('landlordPortal.rentRoll.renewals.pleaseDontReload')}
      />
    );
  } else if (pageState === PAGE_STATES.error) {
    return (
      <Error
        onClick={handleErrorBack}
        headerText={getTranslatedString('landlordPortal.rentRoll.renewals.oops')}
        bodyText={getTranslatedString('landlordPortal.rentRoll.renewals.weWereUnableToProcess')}
        buttonText={getTranslatedString('landlordPortal.rentRoll.renewals.back')}
      />
    );
  }
};

const mapStateToProps = store => ({
  selectedProposal: store.landlord.renewals.selectedProposal,
  renewalDetails: store.landlord.renewals.details,
  cotenants: store.landlord.renewals.leaseCotenants,
  selectedLeaseTerm: store.landlord.renewals.selectedLeaseTerm,
  leaseId: store.landlord.renewals.leaseId,
  leaseDocumentTypes: store.landlord.renewals.lease_document_types,
});

const mapDispatchToProps = {
  storeDetails,
  fetchLeaseCotenants,
  createRenewal,
};

export default connect(mapStateToProps, mapDispatchToProps)(ReviewRenewalDetails);
